import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import CachedIcon from "@mui/icons-material/Cached";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { getAllDdStores } from "../../../app/features/dedicatedReceiving/storeManifestSlice";
import getAxiosError from "../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import { DdStorePerformanceResponse } from "../../../app/models/responseModels/DdStorePerformanceResponse";
import {
  DD_STORE_COLUMN_NAME,
  DD_STORE_LABEL_NAME,
} from "../../dedicatedDelivery/DdTypes";
import CommandOpsDataGrid from "../../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { Box, Grid, IconButton } from "@mui/material";
import Breadcrumb from "../../../components/common/breadcrumb/Breadcrumb";
import { CustomTooltip } from "../../../components/common/tooltip/CustomToolTip";
import GridDataFilter, {
  FilterableColumns,
  FilterableColumnsState,
} from "../../../components/common/filter/GridDataFilter";
import { storeGridFilter } from "../ManifestConstant";
import { useNavigate } from "react-router-dom";
import { formatISODateString } from "../../../components/common/utils/DateUtils";

const breadcrumb = ["Store For Manifest"];

const StoreSelection = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isStoreManifestLoading, ddStores } = useSelector(
    (state: RootState) => state.storeManifest
  );

  const [isTableLoading, setTableLoading] = useState<boolean>(false);

  const [clonedStoreData, setClonedStoreData] =
    useState<DdStorePerformanceResponse[]>(ddStores);

  const getDdStores = useCallback(async () => {
    setTableLoading(true);
    try {
      const response = await dispatch(getAllDdStores()).unwrap();
      if (response.isSuccess) {
        setTableLoading(false);
      } else {
        setTableLoading(false);
        toast.error(response.message, {
          toastId: "allDdStores",
        });
      }
    } catch (error) {
      setTableLoading(false);
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "allDdStoresException",
      });
    }
  }, [dispatch]);

  useEffect(() => {
    setClonedStoreData(ddStores);
  }, [ddStores]);

  useEffect(() => {
    getDdStores();
  }, [getDdStores]);

  const ddStoreColumns = useMemo<MRT_ColumnDef<DdStorePerformanceResponse>[]>(
    () => [
      {
        accessorKey: DD_STORE_COLUMN_NAME.STOREID,
        header: DD_STORE_LABEL_NAME.STOREID,
        size: 10,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.CLIENTSTOREID,
        header: DD_STORE_LABEL_NAME.CLIENTSTOREID,
        size: 10,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.CLIENTID,
        header: DD_STORE_LABEL_NAME.CLIENTID,
        size: 10,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.CLIENTNAME,
        header: DD_STORE_LABEL_NAME.CLIENTNAME,
        size: 20,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.LOCATION,
        header: DD_STORE_LABEL_NAME.LOCATION,
        size: 150,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.TIMEZONE,
        header: DD_STORE_LABEL_NAME.TIMEZONE,
        size: 80,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.FIRSTSERVICE,
        header: DD_STORE_LABEL_NAME.FIRSTSERVICE,
        size: 80,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.LATESTMANIFESTINGESTION,
        header: DD_STORE_LABEL_NAME.LATESTMANIFESTINGESTION,
        size: 80,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
    ],
    []
  );

  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<DdStorePerformanceResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={getDdStores}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  };

  const openManifestDashboardModal = (
    selectedStoreRow: DdStorePerformanceResponse
  ) => {
    navigate(`/store-manifest/${selectedStoreRow.id}`);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <Grid item xs={12} mb={2}>
        <GridDataFilter
          filterableColumns={storeGridFilter}
          title="Search Store"
          asyncSubmitFunction={(filterState: FilterableColumnsState) => {
            if (Object.keys(filterState).length) {
              const filteredData = ddStores.filter(
                (store: Record<string, any>) => {
                  for (const key in filterState) {
                    if (filterState.hasOwnProperty(key)) {
                      const filterValues = filterState[key].values;
                      if (key === "location") {
                        const storeLocation = store.location.replace(
                          /#/g,
                          ", "
                        );
                        if (!filterValues.includes(storeLocation)) {
                          return false;
                        }
                      } else {
                        if (!filterValues.includes(store[key])) {
                          return false;
                        }
                      }
                    }
                  }
                  return true;
                }
              );
              setClonedStoreData(filteredData);
            } else {
              setClonedStoreData(ddStores);
            }
          }}
          asyncSelectedColumnValues={(
            columnName: string,
            setClFilterableColumns: React.Dispatch<
              React.SetStateAction<FilterableColumns>
            >
          ) => {
            let filteredData: string[];
            filteredData = ddStores
              .map((e: Record<string, any>) => e[columnName])
              .filter((value) => value !== undefined && value !== null);
            if (columnName === "location") {
              filteredData = ddStores
                .map((e: Record<string, any>) =>
                  e[columnName].replaceAll("#", ", ")
                )
                .filter((value) => value !== undefined && value !== null);
            }
            filteredData = Array.from(new Set(filteredData));
            setClFilterableColumns((prevState: FilterableColumns) => {
              return {
                ...prevState,
                [columnName]: {
                  ...prevState[columnName],
                  options: filteredData ?? [],
                },
              };
            });
          }}
        />
      </Grid>
      <CommandOpsDataGrid
        data={clonedStoreData}
        columns={ddStoreColumns}
        isLoading={isStoreManifestLoading || isTableLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        openModal={openManifestDashboardModal}
        emptyRowMessage={"No Stores"}
        rowCount={ddStores.length.toLocaleString()}
        isDescending={false}
        defaultSortColumnName={"clientName"}
      />
    </Box>
  );
};

export default StoreSelection;
